import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { FiShoppingCart, FiSearch } from "react-icons/fi";
import { getProducts } from "../../pages/AdminDashboard/Content/services/menuItems.service";
import { TYPES } from "../../pages/AdminDashboard/Content/constants";
import { useCart } from "../../context/CartContext";

const StarIcon = ({ className = "w-5 h-5 text-[#000000]" }) => (
  <svg className={className} aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="currentColor" viewBox="0 0 24 24">
    <path d="M13.849 4.22c-.684-1.626-3.014-1.626-3.698 0L8.397 8.387l-4.552.361c-1.775.14-2.495 2.331-1.142 3.477l3.468 2.937-1.06 4.392c-.413 1.713 1.472 3.067 2.992 2.149L12 19.35l3.897 2.354c1.52.918 3.405-.436 2.992-2.15l-1.06-4.39 3.468-2.938c1.353-1.146.633-3.336-1.142-3.477l-4.552-.36-1.754-4.17Z" />
  </svg>
);

const ListProducts = () => {
  const { addItem } = useCart();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeType, setActiveType] = useState("men");
  const [search, setSearch] = useState("");
  const [toast, setToast] = useState(null);

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      const data = await getProducts();
      setProducts(data);
    } catch (err) {
      console.error("Failed to load products", err);
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = useMemo(() => {
    const legacyTypes = ["perfume", "watch"];
    const byType = products.filter(
      (p) =>
        p.type === activeType ||
        (activeType === "men" && legacyTypes.includes(p.type))
    );
    if (!search.trim()) return byType;
    const term = search.trim().toLowerCase();
    return byType.filter((p) => {
      const name = (p.name || "").toLowerCase();
      const desc = (p.shenime || p.description || "").toLowerCase();
      return name.includes(term) || desc.includes(term);
    });
  }, [products, activeType, search]);

  const showToast = (item) => {
    setToast(item);
    setTimeout(() => setToast(null), 4000);
  };

  const handleAddToCart = (product) => {
    const item = {
      id: product.id,
      name: product.name,
      price: product.price,
      quantity: 1,
      imageUrl: product.imageUrl,
    };
    addItem(item);
    showToast(item);
  };

  if (loading) {
    return (
      <section className="mx-auto max-w-6xl px-4 py-16">
        <div className="flex flex-col items-center justify-center py-20">
          <div className="h-10 w-10 animate-spin rounded-full border-2 border-[#e0d8c3] border-t-transparent" />
          <p className="mt-4 text-[#a6a6a6]">Duke ngarkuar parfumet...</p>
        </div>
      </section>
    );
  }

  return (
    <>
      <section className="mx-auto max-w-6xl px-4 py-12 sm:py-16">
        {/* Section header */}
        <div className="mb-10 flex flex-col gap-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <h2 className="text-2xl font-bold text-[#ffffff] sm:text-3xl">
                Koleksioni
              </h2>
              <div className="mt-2 h-1 w-16 rounded-full bg-[#e0d8c3]" />
            </div>
            <div className="flex gap-2">
            {TYPES.map((t) => {
              const isActive = activeType === t.key;
              return (
                <button
                  key={t.key}
                  type="button"
                  onClick={() => setActiveType(t.key)}
                  className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-all duration-200 ${
                    isActive
                      ? "bg-[#e0d8c3] text-[#000000]"
                      : "border border-[#a6a6a6] bg-transparent text-[#e0d8c3] hover:bg-[#a6a6a6]/20 hover:border-[#e0d8c3]"
                  }`}
                >
                  {t.label}
                </button>
              );
            })}
            </div>
          </div>
          <div className="relative max-w-md">
            <FiSearch className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-[#a6a6a6]" aria-hidden />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Kërko parfume..."
              className="w-full rounded-xl border border-[#a6a6a6]/40 bg-[#000000]/40 py-2.5 pl-10 pr-4 text-[#ffffff] placeholder-[#a6a6a6] focus:border-[#e0d8c3] focus:outline-none focus:ring-2 focus:ring-[#e0d8c3]/30"
              aria-label="Kërko produkte"
            />
          </div>
        </div>

        {filteredProducts.length === 0 ? (
          <div className="rounded-2xl border border-[#a6a6a6]/40 bg-[#ffffff]/5 p-12 text-center">
            <p className="text-[#a6a6a6]">
              {search.trim()
                ? "Nuk u gjet asnjë produkt për kërkimin tuaj."
                : "Aktualisht nuk ka produkte për këtë kategori."}
            </p>
          </div>
        ) : (
          <div className="grid gap-6 sm:gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {filteredProducts.map((item) => {
              const isAvailable = item.isActive !== false;

              return (
                <article
                  key={item.id}
                  className={`w-full max-w-sm overflow-hidden rounded-xl border border-[#a6a6a6]/30 bg-white shadow-sm transition-all duration-300 ${
                    isAvailable
                      ? "hover:border-[#e0d8c3] hover:shadow-md"
                      : "opacity-85"
                  }`}
                >
                  {/* Image - full width & full height, link to details */}
                  <Link to={`/parfume/${item.id}`} className="block">
                    <div className="relative flex h-72 w-full items-center justify-center overflow-hidden rounded-t-xl bg-[#a6a6a6]/10">
                      {item.imageUrl ? (
                        <img
                          src={item.imageUrl}
                          alt={item.name}
                          className={`h-full w-full object-cover transition-all duration-500 ${
                            !isAvailable ? "grayscale opacity-70" : ""
                          }`}
                          loading="lazy"
                        />
                      ) : (
                        <span className="text-sm text-[#a6a6a6]">Pa foto</span>
                      )}
                      {!isAvailable && (
                        <div className="absolute inset-0 flex items-center justify-center rounded-t-xl bg-[#000000]/50">
                          <span className="rounded border border-[#a6a6a6] bg-[#000000]/90 px-3 py-1.5 text-xs font-bold uppercase text-[#e0d8c3]">
                            Mungon në stok
                          </span>
                        </div>
                      )}
                    </div>
                  </Link>

                  <div className="p-6">
                  {/* Stars + badge */}
                  <div className="flex items-center gap-3 mb-4">
                    <div className="flex items-center gap-0.5">
                      {[1, 2, 3, 4, 5].map((i) => (
                        <StarIcon key={i} />
                      ))}
                    </div>
                    <span className="rounded border border-[#a6a6a6]/40 bg-[#e0d8c3]/30 px-1.5 py-0.5 text-xs font-medium text-[#000000]">
                      Cilësi
                    </span>
                  </div>

                  {/* Title - link to details */}
                  <Link to={`/parfume/${item.id}`} className="group">
                    <h3 className="text-xl font-semibold tracking-tight text-[#000000] group-hover:text-[#a6a6a6] transition-colors">
                      {item.name}
                    </h3>
                  </Link>
                  {(item.shenime || item.description) && (
                    <p className="mt-2 text-sm text-[#a6a6a6] line-clamp-2">
                      {item.shenime || item.description}
                    </p>
                  )}

                  {/* Price + Add to cart */}
                  <div className="flex items-center justify-between gap-3 mt-6">
                    <span className="text-3xl font-extrabold text-[#000000]">
                      €{Number(item.price).toFixed(2)}
                    </span>
                    {isAvailable ? (
                      <button
                        type="button"
                        className="inline-flex items-center gap-1.5 rounded-lg border border-transparent bg-[#000000] px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-[#000000]/90 focus:ring-4 focus:ring-[#a6a6a6]/50 focus:outline-none transition"
                        onClick={() => handleAddToCart(item)}
                      >
                        <FiShoppingCart className="w-4 h-4" aria-hidden />
                        Shto në shportë
                      </button>
                    ) : (
                      <span className="rounded-lg border border-[#a6a6a6] bg-[#a6a6a6]/10 px-3 py-2 text-sm font-medium text-[#a6a6a6]">
                        Mungon në stok
                      </span>
                    )}
                  </div>
                  </div>
                </article>
              );
            })}
          </div>
        )}
      </section>

      {toast && (
        <div
          className="fixed top-4 left-1/2 z-50 w-[90%] max-w-sm -translate-x-1/2 animate-slide-in sm:top-auto sm:bottom-6 sm:right-6 sm:left-auto sm:translate-x-0"
          role="status"
        >
          <div className="flex items-center gap-3 rounded-2xl border border-[#a6a6a6]/40 bg-[#ffffff] p-4 shadow-2xl">
            <div className="h-12 w-12 shrink-0 overflow-hidden rounded-xl bg-[#e0d8c3]/30">
              {toast.imageUrl ? (
                <img
                  src={toast.imageUrl}
                  alt={toast.name}
                  className="h-full w-full object-contain"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center text-xs text-[#a6a6a6]">
                  —
                </div>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-[#000000]">
                {toast.name} u shtua në shportë.
              </p>
              <p className="text-xs text-[#a6a6a6]">
                €{Number(toast.price).toFixed(2)} · Shporta u përditësua
              </p>
            </div>
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#e0d8c3]/50 text-[#000000] text-sm font-bold">
              ✓
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ListProducts;
